import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    TextField,
    Switch,
    CircularProgress,
    Snackbar,
    Alert,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    Tooltip,
    InputAdornment,
    CssBaseline,
    ToggleButtonGroup,
    ToggleButton,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Fade,
    Chip,
    Stack
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    DeleteOutlined as DeleteIcon,
    Search as SearchIcon,
    ViewModule as ViewModuleIcon,
    ViewList as ViewListIcon,
    MenuBook as SubjectIcon, // Placeholder for empty state
} from '@mui/icons-material';

import axiosInstance from '../../api/axiosConfig'; 
import { styled, ThemeProvider, alpha } from '@mui/material/styles';
import adminTheme from '../../theme'; 
import PageContainer from '../common/PageContainer';
import AdminPageHero from '../common/AdminPageHero';
import AdminPanelCard from '../common/AdminPanelCard';
import Lottie from 'lottie-react';
import animationData from '../../assets/icons/no-results.json'; 

// --- Styled Components ---

const StyledCard = styled(Card)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    height: "100%",
    borderRadius: theme.spacing(2.5),
    border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
    boxShadow: "none",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
        transform: "translateY(-2px)",
        borderColor: theme.palette.primary.light,
    },
}));

// Keeps descriptions uniform in Grid view
const DescriptionText = styled(Typography)(({ theme }) => ({
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 3, 
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    minHeight: 'calc(1.5em * 3)', 
}));

const FadeTransition = React.forwardRef(function FadeTransition(props, ref) {
    return <Fade ref={ref} {...props} />;
});

// --- Main Component ---

const Subjects = () => {
    // --- Data States ---
    const [iSchoolId, setISchoolId] = useState(() => localStorage.getItem('schoolId'));
    const [subjects, setSubjects] = useState([]);
    const [selectedSubject, setSelectedSubject] = useState(null);
    
    // --- Dialog States ---
    const [dialogOpen, setDialogOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [subjectName, setSubjectName] = useState('');
    const [subjectDescription, setSubjectDescription] = useState('');
    const [subjectStatus, setSubjectStatus] = useState('Active');
    
    // --- UI/UX States ---
    const [loading, setLoading] = useState(true); 
    const [submitting, setSubmitting] = useState(false); 
    const [specificLoading, setSpecificLoading] = useState({}); 
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    
    // --- Filters & Views ---
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [viewMode, setViewMode] = useState('list');

    // --- Helpers ---
    const handleSnackbarOpen = useCallback((message, severity = 'info') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const setActionLoadingState = useCallback((subjectId, isLoading) => {
        setSpecificLoading(prev => ({ ...prev, [subjectId]: isLoading }));
    }, []);

    // --- Data Fetching ---
    const fetchSubjects = useCallback(async (schoolId) => {
        if (!schoolId) return;
        try {
            const response = await axiosInstance.get('/api/subjects', {
                params: { schoolId, all: true },
            });
            const subjectsData = response.data?.data || response.data || [];
            setSubjects(Array.isArray(subjectsData) ? subjectsData : []);
        } catch (error) {
            setSubjects([]); 
            handleSnackbarOpen(error.response?.data?.message || 'Failed to fetch subjects.', 'error');
        }
    }, [handleSnackbarOpen]); 

    // --- Effects ---
    useEffect(() => {
        if (iSchoolId) {
            setLoading(true);
            fetchSubjects(iSchoolId).finally(() => setLoading(false));
        } else {
            setLoading(false);
            setSubjects([]);
        }
    }, [iSchoolId, fetchSubjects]);

    // Handle cross-tab school switching
    useEffect(() => {
        const handleStorageChange = () => {
            const updatedSchoolId = localStorage.getItem('schoolId');
            if (updatedSchoolId && updatedSchoolId !== iSchoolId) {
                setISchoolId(updatedSchoolId);
                setLoading(true); 
                fetchSubjects(updatedSchoolId).finally(() => setLoading(false));
            } else if (!updatedSchoolId) {
                 setISchoolId(null);
                 setSubjects([]);
                 handleSnackbarOpen('School context lost. Please select a school.', 'warning');
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [iSchoolId, fetchSubjects, handleSnackbarOpen]); 

    // --- Dialog Handlers ---
    const handleOpenDialog = (subject = null) => {
        if (subject) {
            setSelectedSubject(subject);
            setIsEditing(true);
            setSubjectName(subject.subjectName);
            setSubjectDescription(subject.description || '');
            setSubjectStatus(subject.status || 'Active');
        } else {
            setSelectedSubject(null);
            setIsEditing(false);
            setSubjectName('');
            setSubjectDescription('');
            setSubjectStatus('Active');
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = useCallback(() => {
        if (!submitting) setDialogOpen(false);
    }, [submitting]);

    // --- CRUD Handlers ---
    const handleSaveSubject = async () => {
        if (!subjectName.trim()) return handleSnackbarOpen('Subject Name is required.', 'warning');
        if (!iSchoolId) return handleSnackbarOpen('Cannot save subject without a selected School.', 'error');

        setSubmitting(true);
        const payload = {
            schoolId: iSchoolId,
            subjectName: subjectName.trim(),
            description: subjectDescription.trim(),
            status: subjectStatus, 
        };

        try {
            if (isEditing && selectedSubject) {
                await axiosInstance.put(`/api/subjects/${selectedSubject.subjectId}`, payload);
                handleSnackbarOpen('Subject updated successfully!', 'success');
            } else {
                await axiosInstance.post('/api/subjects', payload);
                handleSnackbarOpen('Subject added successfully!', 'success');
            }
            await fetchSubjects(iSchoolId); 
            handleCloseDialog(); 
        } catch (error) {
            handleSnackbarOpen(error.response?.data?.message || `Failed to ${isEditing ? 'update' : 'add'} subject.`, 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteSubject = async (subjectId, subjectName) => {
        if (window.confirm(`Are you sure you want to delete "${subjectName}"? This action cannot be undone.`)) {
            setActionLoadingState(subjectId, true);
            try {
                await axiosInstance.delete(`/api/subjects/${subjectId}`);
                handleSnackbarOpen('Subject deleted successfully!', 'success');
                setSubjects(prev => prev.filter(s => s.subjectId !== subjectId));
            } catch (error) {
                handleSnackbarOpen(error.response?.data?.message || 'Failed to delete subject.', 'error');
            } finally {
                setActionLoadingState(subjectId, false);
            }
        }
    };

    const handleStatusToggle = async (subject) => {
        const newStatus = subject.status === 'Active' ? 'Inactive' : 'Active';
        setActionLoadingState(subject.subjectId, true);
        try {
            await axiosInstance.put(`/api/subjects/${subject.subjectId}`, {
                ...subject, 
                status: newStatus 
            });
            handleSnackbarOpen(`Subject set to ${newStatus}.`, 'success');
            setSubjects(prev => prev.map(s => s.subjectId === subject.subjectId ? { ...s, status: newStatus } : s));
        } catch (error) {
            handleSnackbarOpen(error.response?.data?.message || 'Failed to update status.', 'error');
        } finally {
            setActionLoadingState(subject.subjectId, false);
        }
    };

    // --- Memoized Filtered Data ---
    const filteredSubjects = useMemo(() => {
        return subjects.filter((s) => {
            const matchesSearch = s.subjectName.toLowerCase().includes(searchQuery.toLowerCase()) || 
                                 (s.description && s.description.toLowerCase().includes(searchQuery.toLowerCase()));
            const matchesStatus = statusFilter === 'All' || s.status === statusFilter;
            return matchesSearch && matchesStatus;
        });
    }, [subjects, searchQuery, statusFilter]);

    // --- Render Logic ---
    return (
        <ThemeProvider theme={adminTheme}>
            <CssBaseline /> {/* KILLS THE BLACK BACKGROUND BUG GLOBALLY */}

            <PageContainer sx={{ width: '100%' }}>
                <AdminPageHero
                    title="Subject Management"
                    subtitle="Create, update, and organize your school subjects in one place."
                    metrics={[
                        { label: 'Subjects', value: subjects.length },
                        { label: 'Active', value: subjects.filter((s) => s.status === 'Active').length },
                        { label: 'Inactive', value: subjects.filter((s) => s.status !== 'Active').length },
                    ]}
                    actions={
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleOpenDialog()}
                            startIcon={<AddIcon />}
                            disabled={submitting || loading || !iSchoolId}
                            disableElevation
                            sx={{ py: 1, px: 2 }}
                        >
                            Add New Subject
                        </Button>
                    }
                    sx={{ mb: 3 }}
                />

                <AdminPanelCard
                    title="Subjects Directory"
                    subtitle="Search subjects, filter by status, and switch between grid and list views."
                    sx={{ mb: 3 }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                        <Box display="flex" gap={2} flexWrap="wrap" flexGrow={1} maxWidth={600}>
                            <TextField
                                placeholder="Search subjects..."
                                variant="outlined"
                                size="small"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                sx={{
                                    flexGrow: 1,
                                    minWidth: 220,
                                    "& .MuiOutlinedInput-root": { bgcolor: "background.paper" }
                                }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
                                }}
                            />
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel sx={{ bgcolor: "background.paper", px: 0.5 }}>Status</InputLabel>
                                <Select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    label="Status"
                                    sx={{ bgcolor: "background.paper" }}
                                >
                                    <MenuItem value="All">All Statuses</MenuItem>
                                    <MenuItem value="Active">Active</MenuItem>
                                    <MenuItem value="Inactive">Inactive</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(e, val) => val && setViewMode(val)}
                            size="small"
                            sx={{
                                bgcolor: "background.paper",
                                borderRadius: 2,
                                "& .MuiToggleButton-root": {
                                    border: 0,
                                    px: 1.2,
                                },
                                "& .Mui-selected": {
                                    bgcolor: alpha(adminTheme.palette.primary.main, 0.12),
                                    color: "primary.main",
                                }
                            }}
                        >
                            <Tooltip title="Grid View">
                                <ToggleButton value="grid"><ViewModuleIcon /></ToggleButton>
                            </Tooltip>
                            <Tooltip title="List View">
                                <ToggleButton value="list"><ViewListIcon /></ToggleButton>
                            </Tooltip>
                        </ToggleButtonGroup>
                    </Box>
                </AdminPanelCard>

                {/* --- Content Area --- */}
                {loading ? (
                    <AdminPanelCard title="Subjects" subtitle="Loading subject records.">
                        <Stack spacing={2}>
                            {Array.from({ length: 4 }).map((_, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        height: 64,
                                        borderRadius: 1.5,
                                        bgcolor: alpha("#94A3B8", 0.14),
                                    }}
                                />
                            ))}
                        </Stack>
                    </AdminPanelCard>
                ) : !iSchoolId ? (
                     <AdminPanelCard>
                        <Box textAlign="center" py={8}>
                            <Typography variant="h6" color="text.primary" fontWeight="bold">School Not Selected</Typography>
                            <Typography variant="body2" color="text.secondary" mt={1}>Please select a school to manage subjects.</Typography>
                        </Box>
                    </AdminPanelCard>
                ) : filteredSubjects.length > 0 ? (

                    viewMode === "grid" ? (
                        /* --- GRID VIEW --- */
                        <Grid container spacing={3}>
                            {filteredSubjects.map((subject) => {
                                const isItemLoading = specificLoading[subject.subjectId] || false;
                                return (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={subject.subjectId}>
                                    <StyledCard>
                                        <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", p: 3 }}>
                                            
                                            {/* Header */}
                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                                <Typography variant="h6" color="text.primary" fontWeight="bold" noWrap title={subject.subjectName}>
                                                    {subject.subjectName}
                                                </Typography>
                                                <Tooltip title={`Mark as ${subject.status === 'Active' ? 'Inactive' : 'Active'}`}>
                                                    <Switch
                                                        checked={subject.status === 'Active'}
                                                        onChange={() => handleStatusToggle(subject)}
                                                        color="success"
                                                        size="small"
                                                        disabled={isItemLoading || submitting}
                                                        sx={{ mr: -1, mt: -0.5 }}
                                                    />
                                                </Tooltip>
                                            </Box>
                                            
                                            {/* Body */}
                                            <Box mb={2} flexGrow={1}>
                                                <Typography variant="subtitle2" color="text.secondary" mb={0.5}>
                                                    Description
                                                </Typography>
                                                <DescriptionText variant="body2">
                                                    {subject.description || 'No description provided.'}
                                                </DescriptionText>
                                            </Box>

                                            {/* Actions Footer */}
                                            <Box display="flex" justifyContent="flex-end" gap={0.5} pt={2} borderTop={1} borderColor="divider">
                                                <Tooltip title="Edit Subject">
                                                    <IconButton size="small" onClick={() => handleOpenDialog(subject)} disabled={isItemLoading || submitting}>
                                                        <EditIcon fontSize="small" color="action" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete Subject">
                                                    <IconButton size="small" onClick={() => handleDeleteSubject(subject.subjectId, subject.subjectName)} disabled={isItemLoading || submitting}>
                                                        {isItemLoading ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" color="error" />}
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>

                                        </CardContent>
                                    </StyledCard>
                                </Grid>
                            )})}
                        </Grid>
                    ) : (
                        /* --- LIST VIEW --- */
                        <AdminPanelCard
                            title="Subjects List"
                            subtitle="Manage subject details, descriptions, and active status from one table."
                        >
                            <TableContainer
                                component={Paper}
                                elevation={0}
                                sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1.5,
                                    overflow: "hidden",
                                    boxShadow: "none",
                                }}
                            >
                                <Box
                                    sx={{
                                        px: 2,
                                        py: 1.2,
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                        backgroundColor: "#F8FAFC",
                                    }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                        <Chip size="small" variant="outlined" label={`Subjects: ${filteredSubjects.length}`} />
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            label={`Active: ${filteredSubjects.filter((s) => s.status === "Active").length}`}
                                        />
                                    </Stack>
                                </Box>
                                <Table sx={{ minWidth: 680 }}>
                                    <TableHead sx={{ bgcolor: "#F5F7FB" }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600, color: "text.secondary", width: 56 }}>#</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: "text.secondary", width: '25%' }}>Subject Name</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: "text.secondary", width: '45%' }}>Description</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>Status</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: "text.secondary" }} align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredSubjects.map((subject, index) => {
                                            const isItemLoading = specificLoading[subject.subjectId] || false;
                                            return (
                                            <TableRow
                                                key={subject.subjectId}
                                                hover
                                                sx={{
                                                    '&:last-child td, &:last-child th': { border: 0 },
                                                    '&:nth-of-type(even)': { bgcolor: alpha("#0F172A", 0.02) },
                                                }}
                                            >
                                                <TableCell sx={{ color: "text.secondary", fontWeight: 700 }}>{index + 1}</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>{subject.subjectName}</TableCell>
                                                <TableCell sx={{ color: "text.secondary" }}>
                                                    {subject.description ? (
                                                        subject.description.length > 60 ? `${subject.description.substring(0, 60)}...` : subject.description
                                                    ) : '-'}
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={0.8} alignItems="center">
                                                        <Chip
                                                            size="small"
                                                            variant="outlined"
                                                            label={subject.status || "Unknown"}
                                                            sx={{
                                                                minWidth: 70,
                                                                borderColor: subject.status === "Active" ? alpha(adminTheme.palette.success.main, 0.4) : alpha(adminTheme.palette.divider, 0.8),
                                                                color: subject.status === "Active" ? adminTheme.palette.success.main : adminTheme.palette.text.secondary,
                                                                backgroundColor: subject.status === "Active" ? alpha(adminTheme.palette.success.main, 0.1) : "transparent",
                                                            }}
                                                        />
                                                        <Switch
                                                            checked={subject.status === "Active"}
                                                            onChange={() => handleStatusToggle(subject)}
                                                            color="success"
                                                            size="small"
                                                            disabled={isItemLoading || submitting}
                                                        />
                                                    </Stack>
                                                </TableCell>
                                                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" onClick={() => handleOpenDialog(subject)} disabled={isItemLoading || submitting}>
                                                            <EditIcon fontSize="small" color="action" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" onClick={() => handleDeleteSubject(subject.subjectId, subject.subjectName)} disabled={isItemLoading || submitting}>
                                                            {isItemLoading ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" color="error" />}
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        )})}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </AdminPanelCard>
                    )

                ) : (
                    /* --- EMPTY STATE --- */
                    <AdminPanelCard>
                        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                            <Box width={150} height={150} mb={2} opacity={0.8}>
                                {animationData ? (
                                    <Lottie animationData={animationData} loop autoplay style={{ height: "100%", width: "100%" }} />
                                ) : (
                                    <SubjectIcon sx={{ fontSize: 80, color: 'text.disabled' }} />
                                )}
                            </Box>
                            <Typography variant="h6" color="text.primary" fontWeight="bold" gutterBottom>
                                {searchQuery || statusFilter !== "All" ? "No matches found" : "No subjects added yet"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mb={3} textAlign="center" maxWidth={400}>
                                {searchQuery || statusFilter !== "All"
                                    ? "Try adjusting your search or filter settings."
                                    : "Get started by adding your first subject, like 'Mathematics' or 'History'."}
                            </Typography>
                            {!(searchQuery || statusFilter !== "All") && iSchoolId && (
                                <Button variant="contained" onClick={() => handleOpenDialog()} disableElevation>
                                    Add First Subject
                                </Button>
                            )}
                        </Box>
                    </AdminPanelCard>
                )}

                {/* --- ADD / EDIT DIALOG --- */}
                <Dialog
                    open={dialogOpen}
                    onClose={handleCloseDialog}
                    maxWidth="sm"
                    fullWidth
                    TransitionComponent={FadeTransition}
                >
                    <DialogTitle>
                        {isEditing ? 'Edit Subject Details' : 'Add New Subject'}
                    </DialogTitle>
                    <DialogContent dividers>
                        <Box display="flex" flexDirection="column" gap={3} pt={1}>
                            <TextField
                                autoFocus
                                label="Subject Name"
                                placeholder="e.g. Mathematics"
                                fullWidth
                                required
                                value={subjectName}
                                onChange={(e) => setSubjectName(e.target.value)}
                                disabled={submitting}
                            />
                            <TextField
                                label="Description (Optional)"
                                placeholder="Brief summary of the curriculum..."
                                fullWidth
                                multiline
                                minRows={3}
                                maxRows={5} 
                                value={subjectDescription}
                                onChange={(e) => setSubjectDescription(e.target.value)}
                                disabled={submitting}
                            />
                            
                            <FormControl fullWidth disabled={submitting}>
                                <InputLabel sx={{ bgcolor: "background.paper", px: 0.5 }}>Status</InputLabel>
                                <Select
                                    value={subjectStatus}
                                    onChange={(e) => setSubjectStatus(e.target.value)}
                                    label="Status" 
                                    sx={{ bgcolor: "background.paper" }}
                                >
                                    <MenuItem value="Active">Active</MenuItem>
                                    <MenuItem value="Inactive">Inactive</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5 }}>
                        <Button onClick={handleCloseDialog} color="inherit" disabled={submitting}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveSubject}
                            variant="contained"
                            color="primary"
                            disableElevation
                            disabled={submitting || !subjectName.trim()} 
                        >
                            {submitting ? <CircularProgress size={24} color="inherit" /> : (isEditing ? 'Save Changes' : 'Create Subject')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* --- Global Snackbar --- */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={(e, reason) => { if (reason !== 'clickaway') setSnackbar(prev => ({ ...prev, open: false })) }}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                        severity={snackbar.severity}
                        variant="filled"
                        sx={{ width: '100%', color: '#fff', fontWeight: 500, borderRadius: 2 }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>

            </PageContainer>
        </ThemeProvider>
    );
};

export default Subjects;
